import styled, { FlattenSimpleInterpolation } from 'styled-components'

import { HeightClass } from '~/utils/enum/classes'

import { typographyMap } from '../Text/Text.styles'

import { StyledFigure } from '../Icon/style'
import { HierarchyLevel } from './enum'

type TypographyType = {
  [key: string]: FlattenSimpleInterpolation
}

const typography = typographyMap as TypographyType

export const InterfaceElement = styled.div<{
  fitContent: boolean
}>`
  width: ${({ fitContent }) => (fitContent ? 'fit-content' : '100%')};
`

export const ComponentStructureBox = styled.div<{
  hierarchyLevel: HierarchyLevel
  heightClass: HeightClass
  disabled: boolean
  backgroundColor?: string
}>`
  background-color: ${({ theme, hierarchyLevel, backgroundColor }) =>
    backgroundColor
      ? backgroundColor
      : theme.navigationSlider.colorSchemes[hierarchyLevel]};

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 0
    ${({ theme, heightClass }) =>
      theme.navigationSlider.sizes[heightClass].componentPaddingHorizontal}px;

  ${({ disabled }) => disabled && 'cursor: not-allowed;'}
`

export const PageValue = styled.div<{
  heightClass: HeightClass
  alignRight?: boolean
  color?: string
}>`
  color: ${({ color }) => color || 'white'};

  text-align: ${({ alignRight }) => (alignRight ? 'right' : 'left')};

  ${({ theme, heightClass }) =>
    typography[theme.navigationSlider.sizes[heightClass].currentPageTypography]}
`

export const TextCurrentPage = styled.span<{
  heightClass: HeightClass
  color?: string
}>`
  color: ${({ color }) => color || 'white'};

  margin-right: ${({ theme, heightClass }) =>
    theme.navigationSlider.sizes[heightClass].elementsSpacing}px;

  ${({ theme, heightClass }) =>
    typography[
      theme.navigationSlider.sizes[heightClass].currentPageTextTypography
    ]}
`

export const Slash = styled(PageValue)`
  margin-left: 1px;
  width: fit-content;
`

export const TextTotalPages = styled.span<{
  heightClass: HeightClass
}>`
  color: ${({ color }) => color || 'white'};

  margin-left: ${({ theme, heightClass }) =>
    theme.navigationSlider.sizes[heightClass].elementsSpacing}px;

  ${({ theme, heightClass }) =>
    typography[
      theme.navigationSlider.sizes[heightClass].totalPageTextTypography
    ]}
`

export const ComponentStructureElementFrame = styled.div<{
  heightClass: HeightClass
  disabled: boolean
}>`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  height: ${({ theme, heightClass }) =>
    theme.navigationSlider.sizes[heightClass].totalHeight}px;

  ${({ disabled }) => disabled && 'pointer-events: none;'}

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`

export const ArrowContainer = styled.button.attrs({
  type: 'button'
})<{
  heightClass: HeightClass
  $secondary?: boolean
  visible: boolean
  disabled: boolean
  tabIndex: number
  rotation: number
  color?: string
  notFocusable?: boolean
}>`
  margin: 0;
  padding: 0;
  border: none;
  ${({ notFocusable }) => `border-radius: ${notFocusable ? '0' : '100%'};`}
  cursor: pointer;
  background: transparent;

  color: ${({ color }) => color || 'white'};

  width: ${({ theme, heightClass }) =>
    theme.navigationSlider.sizes[heightClass].circleSize}px;

  height: ${({ theme, heightClass }) =>
    theme.navigationSlider.sizes[heightClass].circleSize}px;

  rotate: ${({ rotation }) => rotation}deg;

  visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};

  ${({ disabled, notFocusable }) =>
    (disabled || notFocusable) &&
    `
    padding: 8px;
  `}

  > ${StyledFigure} {
    outline: none;
  }

  &:focus > ${StyledFigure} > svg > path:first-child {
    fill: #abbed1;
  }

  &:focus > ${StyledFigure} > svg > circle {
    stroke: #abbed1;
  }

  &:hover {
    border-color: #abbed1;
  }

  &:focus {
    outline: none;
    border-color: #abbed1;
  }

  &:focus-visible {
    outline: none;
    border-color: #abbed1;
  }
`

export const Arrows = styled.div<{
  heightClass: HeightClass
}>`
  display: flex;

  > * {
    margin-right: ${({ theme, heightClass }) =>
      theme.navigationSlider.sizes[heightClass].elementsSpacing}px;
  }

  > :last-child {
    margin-right: 0 !important;
  }
`

export const BoxInfoTextComponentStructure = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  margin: 0 6px;

  @media (max-width: 768px) {
    width: initial;
  }
`

export const InputContainer = styled.div`
  width: min-content;
  margin-right: 2px;
`

export const StyledInput = styled.input<{
  heightClass: HeightClass
}>`
  ${({ theme, heightClass }) =>
    typography[theme.navigationSlider.sizes[heightClass].currentPageTypography]}

  width: ${({ theme, heightClass }) =>
    theme.navigationSlider.sizes[heightClass].editableValueWidth * 1.5}px;

  color: #303032;
  text-align: center;
  border-radius: 4px;

  ${({ theme, heightClass }) => `
    border-width: ${theme.navigationSlider.sizes[heightClass].editableValueBorder}px;
    border-style: solid;
    border-color: #a0b6ca;
  `}

  &:focus {
    outline: none;
  }
`
